/**
 * Maps simple form data to existing customization data structure
 * Ensures compatibility with existing cart and order systems
 */
export default class SimpleFormDataMapper {
    /**
     * Create a new data mapper
     * 
     * @param {Object} templateReader - Template reader instance
     * @param {Object} options - Mapping options
     */
    constructor(templateReader, options = {}) {
        this.templateReader = templateReader;
        this.options = {
            includeDataUrls: true,
            generateDesignId: true,
            ...options
        };
    }
    
    
    /**
     * Map simple form data to full customization structure
     * 
     * @param {Object} formData - Simple form data
     * @param {Object} uploadedImageData - Uploaded image data from server (with id, url, etc.)
     * @returns {Object} Full customization data structure
     */
    mapToCustomizationData(formData, uploadedImageData = null) {
        const templateInfo = this.templateReader.getTemplateInfo();
        const fieldMapping = this.templateReader.getFieldMapping();
        
        // Generate design ID
        const designId = this.options.generateDesignId ? 
            this.generateDesignId() : 
            formData.designId || 'simple_form_design';
        
        // Base customization structure
        const customizationData = {
            version: "2.0",
            designId: designId,
            template: {
                id: templateInfo.id,
                title: templateInfo.title,
                dimensions: templateInfo.dimensions
            },
            sides: {
                front: { steps: {} },
                back: { steps: {} }
            },
            imageReferences: {},
            previewReferences: {},
            pricing: {
                basePrice: parseFloat(window.stcBasePrice || 0),
                frontExtras: 0,
                backExtras: 0,
                totalExtras: 0,
                total: parseFloat(window.stcBasePrice || 0)
            },
            exportTimestamp: Date.now(),
            exportMetadata: {
                handlerDriven: true,
                exportedAt: new Date().toISOString(),
                exportVersion: "2.0",
                source: "simple_form",
                backgroundRemovalEnabled: formData.background_removal_enabled !== false
            },
            summary: this.generateSummary(formData, templateInfo)
        };
        
        // Process each form field
        Object.keys(formData).forEach(fieldName => {
            const fieldValue = formData[fieldName];
            const mapping = fieldMapping[fieldName];
            
            if (!mapping || !fieldValue) return;
            
            // Skip disabled fields and non-step fields
            if (mapping.enabled === false || fieldName === 'background_removal_enabled') return;
            
            // For image fields, use uploaded image data and include background removal preference
            let imageDataToUse = null;
            if (fieldName === 'user_image' && uploadedImageData) {
                imageDataToUse = {
                    ...uploadedImageData,
                    backgroundRemovalEnabled: formData.background_removal_enabled !== false
                };
            }
            
            // Create step data based on field type
            const stepData = this.createStepData(fieldName, fieldValue, mapping, imageDataToUse);
            
            if (stepData) {
                customizationData.sides[mapping.side].steps[mapping.stepId] = stepData;
            }
        });
        
        return customizationData;
    }
    
    /**
     * Create step data for a specific field
     * 
     * @param {string} fieldName - Form field name
     * @param {*} fieldValue - Field value
     * @param {Object} mapping - Field mapping configuration
     * @param {Object} imageData - Image data if applicable
     * @returns {Object} Step data object
     */
    createStepData(fieldName, fieldValue, mapping, imageData) {
        const baseStepData = {
            stepId: mapping.stepId,
            type: mapping.type,
            enabled: true,
            source: 'simple_form'
        };
        
        switch (mapping.type) {
            case 'text':
                return {
                    ...baseStepData,
                    data: {
                        text: fieldValue.toString().trim()
                    }
                };
                
            case 'image':
                return this.createImageStepData(fieldName, fieldValue, mapping, imageData, baseStepData);
                
            default:
                console.warn(`Unknown field type: ${mapping.type}`);
                return null;
        }
    }
    
    
/**
     * Create image step data
     * 
     * @param {string} fieldName - Form field name
     * @param {*} fieldValue - Field value (File object or data URL)
     * @param {Object} mapping - Field mapping
     * @param {Object} imageData - Processed image data from server upload
     * @param {Object} baseStepData - Base step data
     * @returns {Object} Image step data
     */
    createImageStepData(fieldName, fieldValue, mapping, imageData, baseStepData) {
        // Generate unique reference ID for this image
        const referenceId = `${fieldName}_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`;
        
        const imageStepData = {
            ...baseStepData,
            imageContent: {
                imageSource: {
                    id: imageData?.id || null,
                    type: 'user_upload',
                    url: imageData?.url || null,
                    referenceId: referenceId,
                    metadata: {
                        originalName: imageData?.originalName || fieldValue?.name || 'user_image.jpg',
                        fileName: imageData?.fileName || `${fieldName}_${Date.now()}.jpg`,
                        fileType: imageData?.fileType || fieldValue?.type || 'image/jpeg',
                        fileSize: imageData?.fileSize || fieldValue?.size || 0,
                        uploadTimestamp: Date.now(),
                        fieldName: fieldName,
                        source: 'simple_form',
                        backgroundRemovalEnabled: imageData?.backgroundRemovalEnabled !== false
                    }
                },
                cropping: {},
                processing: {
                    needsBackgroundRemoval: imageData?.backgroundRemovalEnabled !== false,
                    backgroundRemovalEnabled: imageData?.backgroundRemovalEnabled !== false,
                    userPreference: imageData?.backgroundRemovalEnabled !== false ? 'enabled' : 'disabled'
                }
            }
        };
        
        return imageStepData;
    }
    
    /**
     * Add image reference to customization data
     * 
     * @param {Object} customizationData - Customization data object
     * @param {string} referenceId - Image reference ID
     * @param {Object} imageData - Image data from server upload
     * @param {string} fieldName - Form field name
     */
    addImageReference(customizationData, referenceId, imageData, fieldName) {
        customizationData.imageReferences[referenceId] = {
            referenceId: referenceId,
            id: imageData?.id || null,
            url: imageData?.url || null,
            type: 'user_upload',
            side: 'front',
            stepId: fieldName === 'user_image' ? 'image' : fieldName,
            needsProcessing: false, // Already processed by server upload
            metadata: {
                ...imageData?.metadata,
                fieldName: fieldName,
                source: 'simple_form'
            }
        };
        
        // Don't include data URLs for server-uploaded images
        // as they're not needed and make the JSON large
    }
    
    /**
     * Generate design summary
     * 
     * @param {Object} formData - Form data
     * @param {Object} templateInfo - Template information
     * @returns {Object} Design summary
     */
    generateSummary(formData, templateInfo) {
        const enabledFields = Object.keys(formData).filter(key => 
            formData[key] && formData[key].toString().trim() !== '' && key !== 'background_removal_enabled'
        );
        
        const hasImages = enabledFields.includes('user_image');
        
        return {
            templateName: templateInfo.title,
            frontCustomizationCount: enabledFields.length,
            backCustomizationCount: 0,
            totalCustomizationCount: enabledFields.length,
            hasImages: hasImages,
            imageCount: hasImages ? 1 : 0,
            hasPreviewImages: false,
            price: {
                base: parseFloat(window.stcBasePrice || 0),
                extras: 0,
                total: parseFloat(window.stcBasePrice || 0)
            },
            fields: enabledFields,
            source: 'simple_form',
            backgroundRemovalEnabled: formData.background_removal_enabled !== false
        };
    }
    
    /**
     * Generate unique design ID
     * 
     * @returns {string} Unique design ID
     */
    generateDesignId() {
        return 'simple_' + Math.random().toString(36).substring(2, 15) + 
               Math.random().toString(36).substring(2, 15) + 
               '_' + Date.now();
    }
    
    /**
     * Validate mapped data structure
     * 
     * @param {Object} customizationData - Mapped customization data
     * @returns {Object} Validation result
     */
    validateMappedData(customizationData) {
        const errors = [];
        const warnings = [];
        
        // Check required structure
        if (!customizationData.template || !customizationData.template.id) {
            errors.push('Missing template information');
        }
        
        if (!customizationData.sides || !customizationData.sides.front) {
            errors.push('Missing front side data');
        }
        
        // Check for required steps
        const frontSteps = customizationData.sides.front.steps || {};
        
        if (!frontSteps.name || !frontSteps.name.data || !frontSteps.name.data.text) {
            errors.push('Missing required name field');
        }
        
        if (!frontSteps.image || !frontSteps.image.imageContent) {
            errors.push('Missing required image field');
        }
        
        // Check image references
        if (customizationData.imageReferences) {
            Object.values(customizationData.imageReferences).forEach(imageRef => {
                if (!imageRef.referenceId) {
                    warnings.push('Image reference missing referenceId');
                }
                
                if (imageRef.needsProcessing && !imageRef.dataUrl && !imageRef.url) {
                    errors.push('Image reference needs processing but has no data URL or URL');
                }
            });
        }
        
        return {
            isValid: errors.length === 0,
            errors: errors,
            warnings: warnings
        };
    }
    
    /**
     * Convert mapped data for cart submission
     * Removes large data URLs and temporary data
     * 
     * @param {Object} customizationData - Full customization data
     * @returns {Object} Clean data for cart
     */
    prepareForCart(customizationData) {
        // Create a deep copy
        const cartData = JSON.parse(JSON.stringify(customizationData));
        
        // Remove large data URLs from image references to keep JSON small
        if (cartData.imageReferences) {
            Object.keys(cartData.imageReferences).forEach(key => {
                const imgRef = cartData.imageReferences[key];
                
                // Keep small identifiers but remove large data
                if (imgRef.dataUrl && imgRef.dataUrl.length > 1000) {
                    delete imgRef.dataUrl;
                }
            });
        }
        
        return cartData;
    }
    
    /**
     * Extract form data from DOM form
     * 
     * @param {HTMLFormElement} form - Form element
     * @returns {Object} Extracted form data
     */
    extractFormData(form) {
        const formData = {};
        const formElements = form.elements;
        
        for (let i = 0; i < formElements.length; i++) {
            const element = formElements[i];
            
            if (!element.name || element.disabled) continue;
            
            switch (element.type) {
                case 'text':
                case 'textarea':
                    if (element.value.trim()) {
                        formData[element.name] = element.value.trim();
                    }
                break;
                    
                case 'file':
                    if (element.files && element.files[0]) {
                        formData[element.name] = element.files[0];
                    }
                    break;
            }
        }
        
        return formData;
    }
}